import { BasePortalOutlet, CdkPortalOutlet, ComponentPortal, TemplatePortal } from "@angular/cdk/portal";
import {
    ChangeDetectorRef,
    Component,
    ComponentRef,
    DOCUMENT,
    ElementRef,
    EmbeddedViewRef,
    inject,
    NgZone,
    OnDestroy,
    viewChild,
    ViewEncapsulation
} from "@angular/core";
import { Observable, of, Subject } from "rxjs";
import { VdsToastConfig } from "../toast.config";
import { Platform } from "@angular/cdk/platform";
import { _IdGenerator, AriaLivePoliteness } from "@angular/cdk/a11y";

const ENTER_ANIMATION = "vds-toast-enter";
const EXIT_ANIMATION = "vds-toast-exit";

/**
 * Internal component that wraps user-provided toast content.
 * @docs-private
 */
@Component({
    selector: "vds-toast-container",
    templateUrl: "./container.component.html",
    styleUrl: "./container.component.css",
    encapsulation: ViewEncapsulation.None,
    host: {
        "[attr.role]": "_role",
        class: "vds-toast-container",
        "[class.vds-toast-container--enter]": "_animationState === 'visible'",
        "[class.vds-toast-container--exit]": "_animationState === 'hidden'",
        "(animationend)": "onAnimationEnd($event.animationName)",
        "(animationcancel)": "onAnimationEnd($event.animationName)"
    },
    imports: [CdkPortalOutlet]
})
export class VdsToastContainerComponent extends BasePortalOutlet implements OnDestroy {
    private ngZone = inject(NgZone);
    private elementRef = inject(ElementRef<HTMLElement>);
    private changeDetectorRef = inject(ChangeDetectorRef);
    private platform = inject(Platform);
    toastConfig = inject(VdsToastConfig);

    private document = inject(DOCUMENT);
    private _trackedModals = new Set<Element>();

    private _enterFallback: ReturnType<typeof setTimeout> | undefined;
    private _exitFallback: ReturnType<typeof setTimeout> | undefined;

    /** The number of milliseconds to wait before announcing the toasts content. */
    private readonly _announceDelay: number = 150;

    /** The timeout for announcing the toasts content. */
    private _announceTimeoutId: ReturnType<typeof setTimeout> | undefined;

    /** Whether the component has been destroyed. */
    private _destroyed = false;

    /** The portal outlet inside of this container into which the toast content will be loaded. */
    private portalOutlet = viewChild.required(CdkPortalOutlet);

    /** Subject for notifying that the toast has announced to screen readers. */
    readonly _onAnnounce: Subject<void> = new Subject();

    /** Subject for notifying that the toast has exited from view. */
    readonly _onExit: Subject<void> = new Subject<void>();

    /** Subject for notifying that the toast has finished entering the view. */
    readonly _onEnter: Subject<void> = new Subject<void>();

    /** The state of the toast animations. */
    _animationState = "void";

    /** aria-live value for the live region. */
    _live: AriaLivePoliteness;

    /** ARIA role for the toast container. */
    _role?: "status" | "alert";

    /** Unique ID of the aria-live element. */
    readonly _liveElementId = inject(_IdGenerator).getId("vds-toast-container-live-");

    constructor() {
        super();
        const config = this.toastConfig;

        // Use aria-live rather than a live role like 'alert' or 'status'
        // because NVDA and JAWS have show inconsistent behavior with live roles.
        if (config.politeness === "assertive" && !config.announcementMessage) {
            this._live = "assertive";
        } else if (config.politeness === "off") {
            this._live = "off";
        } else {
            this._live = "polite";
        }

        // Only set role for Firefox. Set role based on aria-live because setting role="alert" implies
        // aria-live="assertive" which may cause issues if aria-live is set to "polite" above.
        if (this.platform.FIREFOX) {
            if (this._live === "polite") {
                this._role = "status";
            }
            if (this._live === "assertive") {
                this._role = "alert";
            }
        }
    }

    /** Attach a component portal as content to this toast container. */
    attachComponentPortal<T>(portal: ComponentPortal<T>): ComponentRef<T> {
        this._assertNotAttached();
        const result = this.portalOutlet().attachComponentPortal(portal);
        this._afterPortalAttached();
        return result;
    }

    /** Attach a template portal as content to this toast container. */
    attachTemplatePortal<C>(portal: TemplatePortal<C>): EmbeddedViewRef<C> {
        this._assertNotAttached();
        const result = this.portalOutlet().attachTemplatePortal(portal);
        this._afterPortalAttached();
        return result;
    }

    /** Handle end of animations, updating the state of the toast. */
    onAnimationEnd(animationName: string) {
        if (animationName === EXIT_ANIMATION) {
            this._completeExit();
        } else if (animationName === ENTER_ANIMATION) {
            clearTimeout(this._enterFallback);
            this.ngZone.run(() => {
                this._onEnter.next();
                this._onEnter.complete();
            });
        }
    }

    /** Begin animation of toast entrance into view. */
    enter(): void {
        if (!this._destroyed) {
            this._animationState = "visible";
            this.changeDetectorRef.markForCheck();
            this.changeDetectorRef.detectChanges();

            clearTimeout(this._enterFallback);
            this._enterFallback = setTimeout(() => {
                // The toast will stay invisible if it fails to animate. Add a fallback class so it
                // becomes visible. This can happen in some apps that do `* {animation: none !important}`.
                this.elementRef.nativeElement.classList.add("vds-toast-container--fallback-visible");
                this.onAnimationEnd(ENTER_ANIMATION);
            }, 200);

            this._screenReaderAnnounce();
        }
    }

    /** Begin animation of the toast exiting from view. */
    exit(): Observable<void> {
        if (this._destroyed) {
            return of(undefined);
        }

        this.ngZone.run(() => {
            this._animationState = "hidden";
            this.changeDetectorRef.markForCheck();

            clearTimeout(this._announceTimeoutId);

            clearTimeout(this._exitFallback);
            this._exitFallback = setTimeout(() => this.onAnimationEnd(EXIT_ANIMATION), 200);
        });

        return this._onExit;
    }

    /** Makes sure the exit callbacks have been invoked when the element is destroyed. */
    ngOnDestroy() {
        this._destroyed = true;
        this._clearFromModals();
        this._completeExit();
    }

    /**
     * Waits for the zone to settle before removing the element. Helps prevent
     * errors where we end up removing an element which is in the middle of an animation.
     */
    private _completeExit() {
        clearTimeout(this._exitFallback);
        queueMicrotask(() => {
            this._onExit.next();
            this._onExit.complete();
        });
    }

    /** Applies the various positioning and user-configured CSS classes to the toast. */
    private _afterPortalAttached() {
        const element: HTMLElement = this.elementRef.nativeElement;
        const panelClasses = this.toastConfig.panelClass;

        if (panelClasses) {
            if (Array.isArray(panelClasses)) {
                // Note that we can't use a spread here, because IE doesn't support multiple arguments.
                panelClasses.forEach((cssClass) => element.classList.add(cssClass));
            } else {
                element.classList.add(panelClasses);
            }
        }

        this._exposeToModals();

        if (this.toastConfig.horizontalPosition === "center") {
            element.classList.add("vds-toast--center");
        }

        if (this.toastConfig.verticalPosition === "top") {
            element.classList.add("vds-toast--top");
        }
    }

    private _exposeToModals() {
        const id = this._liveElementId;
        const modals = this.document.querySelectorAll("body > .cdk-overlay-container [aria-modal='true']");

        for (let i = 0; i < modals.length; i++) {
            const modal = modals[i];
            const ariaOwns = modal.getAttribute("aria-owns");
            this._trackedModals.add(modal);

            if (!ariaOwns) {
                modal.setAttribute("aria-owns", id);
            } else if (ariaOwns.indexOf(id) === -1) {
                modal.setAttribute("aria-owns", ariaOwns + " " + id);
            }
        }
    }

    private _clearFromModals() {
        this._trackedModals.forEach((modal) => {
            const ariaOwns = modal.getAttribute("aria-owns");

            if (ariaOwns) {
                const newValue = ariaOwns.replace(this._liveElementId, "").trim();

                if (newValue.length > 0) {
                    modal.setAttribute("aria-owns", newValue);
                } else {
                    modal.removeAttribute("aria-owns");
                }
            }
        });

        this._trackedModals.clear();
    }

    /** Asserts that no content is already attached to the container. */
    private _assertNotAttached() {
        if (this.portalOutlet().hasAttached()) {
            throw Error("Attempting to attach toast content after content is already attached");
        }
    }

    private _screenReaderAnnounce() {
        if (this._announceTimeoutId) {
            return;
        }

        this.ngZone.runOutsideAngular(() => {
            this._announceTimeoutId = setTimeout(() => {
                if (this._destroyed) {
                    return;
                }

                const element = this.elementRef.nativeElement;
                const inertElement = element.querySelector("[aria-hidden]");
                const liveElement = element.querySelector("[aria-live]");

                if (inertElement && liveElement) {
                    let focusedElement: HTMLElement | null = null;
                    if (
                        this.platform.isBrowser &&
                        document.activeElement instanceof HTMLElement &&
                        inertElement.contains(document.activeElement)
                    ) {
                        focusedElement = document.activeElement;
                    }

                    inertElement.removeAttribute("aria-hidden");
                    liveElement.appendChild(inertElement);
                    focusedElement?.focus();

                    this._onAnnounce.next();
                    this._onAnnounce.complete();
                }
            }, this._announceDelay);
        });
    }
}
